import { Search, Filter, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ToolStatus } from '@/enums/tool-status.enum'
import { cn } from '@/lib/utils'
import {
  Button,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'

type StatusFilter = Nullable<ToolStatus>

interface SummarizationSearchBarProps {
  searchTerm: string
  statusFilter: StatusFilter
  onSearchChange: (value: string) => void
  onStatusFilterChange: (filter: StatusFilter) => void
}

export function SummarizationSearchBar({
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
}: SummarizationSearchBarProps) {
  const { t } = useTranslation()

  return (
    <div className='flex items-center justify-end gap-4 mb-6 flex-wrap'>
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
        <Input
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
          className={cn('pl-10', searchTerm && 'pr-10')}
        />
        {searchTerm && (
          <button
            type='button'
            onClick={() => onSearchChange('')}
            className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors'
            aria-label={t('common.clear')}
          >
            <X className='h-4 w-4' />
          </button>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline' className='gap-2 relative'>
            {t('common.filter')}
            <Filter className='h-4 w-4' />
            {statusFilter && (
              <span className='absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-medium'>
                {t(`toolManagement.${statusFilter}`, statusFilter)
                  .charAt(0)
                  .toUpperCase()}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onClick={() => onStatusFilterChange(ToolStatus.Available)}
            className={cn(statusFilter === ToolStatus.Available && 'bg-accent')}
          >
            {t('toolManagement.available')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onStatusFilterChange(ToolStatus.Borrowed)}
            className={cn(statusFilter === ToolStatus.Borrowed && 'bg-accent')}
          >
            {t('toolManagement.borrowed')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onStatusFilterChange(ToolStatus.Missing)}
            className={cn(statusFilter === ToolStatus.Missing && 'bg-accent')}
          >
            {t('toolManagement.missing')}
          </DropdownMenuItem>
          {statusFilter && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onStatusFilterChange(null)}>
                {t('common.clearFilter')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {statusFilter && (
        <div className='flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800'>
          <span className='font-medium'>
            {t('common.activeFilters', 'Active filters')}:
          </span>
          {searchTerm && (
            <span className='px-2 py-0.5 bg-blue-100 rounded text-blue-900'>
              {t('common.search', 'Search')}: &quot;{searchTerm}&quot;
            </span>
          )}
          {statusFilter && (
            <span className='px-2 py-0.5 bg-blue-100 rounded text-blue-900'>
              {t(`toolManagement.${statusFilter}`, statusFilter)}
            </span>
          )}
          <button
            type='button'
            onClick={() => {
              onSearchChange('')
              onStatusFilterChange(null)
            }}
            className='ml-2 text-blue-600 hover:text-blue-800 transition-colors'
            aria-label={t('common.clearAllFilters', 'Clear all filters')}
          >
            {t('common.clearAll', 'Clear all')}
          </button>
        </div>
      )}
    </div>
  )
}
